import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listarPacientes,
  obtenerPaciente,
  actualizarPaciente,
  eliminarPaciente,
  listarEspecialidades,
} from "../api";
import "./PacientesList.css";

// 🔹 Util para formatear fechas a dd-mm-yyyy (solo para render)
const formatDate = (val) => {
  if (!val) return "-";
  if (typeof val === "string" && /^\d{2}-\d{2}-\d{4}$/.test(val)) return val;
  const d = new Date(val);
  if (!isNaN(d)) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(val));
  if (m) {
    const [_, y, mo, da] = m;
    return `${String(da).padStart(2, "0")}-${String(mo).padStart(2, "0")}-${y}`;
  }
  return String(val);
};

export default function PacientesList({ userRole }) {
  const navigate = useNavigate();
  const isAdmin = userRole === "admin";

  const basePath =
    userRole === "admin"
      ? "/admin"
      : userRole === "psicologa"
      ? "/psicologia"
      : userRole === "logopeda"
      ? "/logopedia"
      : userRole === "nutricionista"
      ? "/nutricion"
      : "";

  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState({ items: [], total: 0, pages: 1, page: 1 });

  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [current, setCurrent] = useState(0);

  // ✅ catálogo de especialidades (dinámico)
  const [allSpecs, setAllSpecs] = useState([]); // [{id,code,nombre}]

  const [form, setForm] = useState({
    nombre: "",
    fechaNacimiento: "",
    sexo: "",
    colegio: "",
    direccion: "",
    servicios: {
      psicologia: false,
      logopedia: false,
      administracion: false,
      nutricion: false,
    },
    specialty_ids: [],
    observaciones: "",
    tutor: "",
    tutor_dni: "",
    tutor_telefono: "",
    email: "",
    emergencia: "",
  });
  const [msg, setMsg] = useState("");

  // 🧠 Filtro por especialidad (checkboxes)
  const [filtros, setFiltros] = useState({
    psicologia: false,
    logopedia: false,
    nutricion: false,
    administracion: false,
  });

  // ✅ filtros activos para backend (codes)
  const activeFilterCodes = useMemo(() => {
    return Object.entries(filtros)
      .filter(([_, v]) => v)
      .map(([k]) => k);
  }, [filtros]);

  // --- búsqueda con debounce ---
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(typed);
      setPage(1);
      setCurrent(0);
    }, 400);
    return () => clearTimeout(t);
  }, [typed]);

  // ✅ cuando cambian filtros, vuelve a página 1 y resetea carrusel
  useEffect(() => {
    setPage(1);
    setCurrent(0);
  }, [activeFilterCodes.join("|")]);

  // ✅ cargar especialidades (una vez)
  useEffect(() => {
    (async () => {
      try {
        const r = await listarEspecialidades();
        console.log("🧩 specialties.list:", r);
        setAllSpecs(Array.isArray(r.items) ? r.items : []);
      } catch (e) {
        console.error("❌ Error cargando especialidades", e);
        setAllSpecs([]);
      }
    })();
  }, []);

  // --- cargar pacientes ---
  useEffect(() => {
    let alive = true;
    setLoading(true);

    listarPacientes({
      q,
      page,
      page_size: 10,
      specialty_codes: activeFilterCodes,
    })
      .then((r) => {
        console.log("🔍 Respuesta del backend (listarPacientes):", r);
        if (!alive) return;

        const items = Array.isArray(r.items)
          ? r.items
          : Array.isArray(r.pacientes)
          ? r.pacientes
          : [];

        setRes({
          items,
          total: r.total ?? items.length,
          pages: r.pages || 1,
          page: r.page || page,
        });

        const newPages = r.pages || 1;
        const newPage = r.page || page;
        if (newPage > newPages) setPage(1);

        setCurrent(0);
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [q, page, activeFilterCodes.join("|")]);

  const pages = res.pages || 1;
  const hasPrev = page > 1;
  const hasNext = page < pages;

  // --- helpers para normalizar especialidades del paciente desde obtenerPaciente() ---
  const extractSpecialtyIds = (p) => {
    if (Array.isArray(p?.specialty_ids) && p.specialty_ids.length)
      return p.specialty_ids.map(Number);

    if (Array.isArray(p?.specialties) && p.specialties.length) {
      const first = p.specialties[0];
      if (typeof first === "object" && first) {
        const ids = p.specialties
          .map((s) => Number(s.id || s.specialty_id || s.specialtyId))
          .filter(Boolean);
        if (ids.length) return ids;
      }
    }

    if (Array.isArray(p?.patient_specialties) && p.patient_specialties.length) {
      const ids = p.patient_specialties
        .map((x) => Number(x.specialty_id || x.specialtyId || x?.specialty?.id))
        .filter(Boolean);
      if (ids.length) return ids;
    }

    if (
      Array.isArray(p?.specialty_codes) &&
      p.specialty_codes.length &&
      allSpecs.length
    ) {
      const codes = p.specialty_codes.map(normalizeSpecialtyCode);
      const ids = allSpecs
        .filter((s) =>
          codes.includes(normalizeSpecialtyCode(s.code)),
        )
        .map((s) => s.id);
      if (ids.length) return ids;
    }

    return [];
  };

  const normalizeSpecialtyCode = (code) => {
    const normalized = String(code || "").trim().toLowerCase();

    if (normalized === "logopeda") {
      return "logopedia";
    }

    return normalized;
  };

  const serviciosToCodes = (serviciosNorm) => {
    return Object.entries(serviciosNorm)
      .filter(([_, value]) => value)
      .map(([key]) => normalizeSpecialtyCode(key));
  };

  // --- abrir modal de edición ---
  const openEdit = async (id) => {
    try {
      setMsg("");
      const p = await obtenerPaciente(id);

      let serviciosNorm = {
        psicologia: false,
        logopedia: false,
        administracion: false,
        nutricion: false,
      };
      if (Array.isArray(p.servicios)) {
        p.servicios.forEach((serviceCode) => {
          const code = normalizeSpecialtyCode(serviceCode);

          if (
            Object.prototype.hasOwnProperty.call(
              serviciosNorm,
              code,
            )
          ) {
            serviciosNorm[code] = true;
          }
        });
      } else if (
        typeof p.servicios === "object" &&
        p.servicios !== null
      ) {
        Object.entries(p.servicios).forEach(
          ([serviceCode, enabled]) => {
            const code = normalizeSpecialtyCode(serviceCode);

            if (
              Object.prototype.hasOwnProperty.call(
                serviciosNorm,
                code,
              )
            ) {
              serviciosNorm[code] = Boolean(enabled);
            }
          },
        );
      } else if (typeof p.servicios === "string") {
        const parts = p.servicios
          .split(",")
          .map(normalizeSpecialtyCode);

        parts.forEach((code) => {
          if (
            Object.prototype.hasOwnProperty.call(
              serviciosNorm,
              code,
            )
          ) {
            serviciosNorm[code] = true;
          }
        });
      }

      let specialty_ids = extractSpecialtyIds(p);

      if ((!specialty_ids || specialty_ids.length === 0) && allSpecs.length) {
        const legacyCodes = serviciosToCodes(serviciosNorm);
        specialty_ids = allSpecs
          .filter((s) => legacyCodes.includes(s.code))
          .map((s) => s.id);
      }

      setEditId(p.id);
      setForm({
        nombre: p.nombre || "",
        fechaNacimiento: p.fechaNacimiento || p.fecha_nacimiento || "",
        sexo: p.sexo || "",
        colegio: p.colegio || "",
        direccion: p.direccion || "",
        servicios: serviciosNorm,
        specialty_ids,
        observaciones: p.observaciones || "",
        tutor: p.tutor?.nombre || p.tutor || "",
        tutor_dni: p.tutor?.dni || "",
        tutor_telefono: p.tutor?.telefono || "",
        email: p.tutor?.email || p.email || "",
        emergencia: p.tutor?.emergencia || p.emergencia || "",
      });
      setEditOpen(true);
    } catch (e) {
      alert(e.message || "No se pudieron cargar los datos");
    }
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditId(null);
    setMsg("");
  };

  const toggleSpecialtyId = (id, checked) => {
    const sid = Number(id);
    setForm((prev) => {
      const set = new Set(prev.specialty_ids || []);
      if (checked) set.add(sid);
      else set.delete(sid);

      const spec = allSpecs.find((s) => Number(s.id) === sid);
      const legacyKey = normalizeSpecialtyCode(spec?.code);
      const nextServicios = { ...prev.servicios };
      if (
        legacyKey &&
        Object.prototype.hasOwnProperty.call(nextServicios, legacyKey)
      ) {
        nextServicios[legacyKey] = checked;
      }

      return {
        ...prev,
        specialty_ids: Array.from(set),
        servicios: nextServicios,
      };
    });
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (
      type === "checkbox" &&
      Object.prototype.hasOwnProperty.call(form.servicios, name)
    ) {
      setForm((prev) => ({
        ...prev,
        servicios: { ...prev.servicios, [name]: checked },
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.nombre.trim()) return "El nombre del paciente es obligatorio";
    if (!form.fechaNacimiento) return "La fecha de nacimiento es obligatoria";
    if (!form.sexo) return "Selecciona el sexo";

    if (!Array.isArray(form.specialty_ids) || form.specialty_ids.length === 0) {
      return "Selecciona al menos una especialidad";
    }

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      return "Email del tutor no válido";
    return null;
  };

  const reloadList = async () => {
    const r = await listarPacientes({
      q,
      page,
      page_size: 10,
      specialty_codes: activeFilterCodes,
    });
    const items = Array.isArray(r.items)
      ? r.items
      : Array.isArray(r.pacientes)
      ? r.pacientes
      : [];
    setRes({
      items,
      total: r.total ?? items.length,
      pages: r.pages || 1,
      page: r.page || page,
    });
    setCurrent(0);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setMsg(err);
      return;
    }
    setSaving(true);
    try {
      const selectedServicios = Object.entries(form.servicios)
        .filter(([_, value]) => value)
        .map(([key]) => normalizeSpecialtyCode(key));

      const payload = {
        nombre: form.nombre,
        fechaNacimiento: form.fechaNacimiento,
        sexo: form.sexo,
        colegio: form.colegio,
        direccion: form.direccion,
        specialty_ids: form.specialty_ids,
        servicios: selectedServicios,
        observaciones: form.observaciones,
        tutor: form.tutor,
        tutor_dni: form.tutor_dni,
        tutor_telefono: form.tutor_telefono,
        email: form.email,
        emergencia: form.emergencia,
      };

      await actualizarPaciente(editId, payload);
      setMsg("Paciente actualizado ✅");

      await reloadList();
      setTimeout(() => closeEdit(), 500);
    } catch (e2) {
      setMsg(e2.message || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Seguro que quieres eliminar este paciente?")) return;
    try {
      await eliminarPaciente(id);
      await reloadList();
    } catch (e) {
      alert(e.message || "Error al eliminar paciente");
    }
  };

  const goView = (id) => {
    navigate(`/paciente/${id}`);
  };

  const items = Array.isArray(res.items) ? res.items : [];

  return (
    <div className="pl-wrap">
      <div className="pl-tools">
        <input
          type="search"
          placeholder="Buscar pacientes…"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        <span className="pl-count">
          {loading
            ? "Cargando…"
            : `${res.total || 0} resultado${res.total === 1 ? "" : "s"}`}
        </span>
      </div>

      <div className="pl-filtros">
        {["psicologia", "logopedia", "nutricion", "administracion"].map((k) => (
          <label key={k}>
            <input
              type="checkbox"
              checked={!!filtros[k]}
              onChange={(e) =>
                setFiltros((prev) => ({ ...prev, [k]: e.target.checked }))
              }
            />
            {k === "logopedia"
              ? "Logopedia"
              : k.charAt(0).toUpperCase() + k.slice(1)}
          </label>
        ))}
      </div>

      <div className="desktop-only pl-table-scroll">
        <table className="pl-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Nacimiento</th>
              <th>Sexo</th>
              {isAdmin && <th>Colegio</th>}
              <th>Servicios</th>
              <th>Tutor</th>
              {isAdmin && <th>Contacto</th>}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 8 : 6} className="pl-empty">
                  Sin resultados
                </td>
              </tr>
            )}

            {items.map((p) => (
              <tr key={p.id}>
                <td>{p.nombre}</td>
                <td>{formatDate(p.fechaNacimiento || p.fecha_nacimiento)}</td>
                <td>{p.sexo || "-"}</td>

                {isAdmin && <td>{p.colegio || "-"}</td>}

                <td>
                  {Array.isArray(p.servicios)
                    ? p.servicios
                        .map(normalizeSpecialtyCode)
                        .join(", ")
                    : typeof p.servicios === "string"
                      ? p.servicios
                          .split(",")
                          .map(normalizeSpecialtyCode)
                          .join(", ")
                      : "-"}
                </td>

                <td>
                  {p.tutor
                    ? typeof p.tutor === "object"
                      ? p.tutor.nombre || "-"
                      : p.tutor
                    : "-"}
                </td>

                {isAdmin && (
                  <td>
                    {p.tutor ? (
                      typeof p.tutor === "object" ? (
                        <>
                          {p.tutor.telefono || "-"}
                          {p.tutor.email && (
                            <div className="pl-sub">{p.tutor.email}</div>
                          )}
                        </>
                      ) : (
                        <>
                          {p.telefono || "-"}
                          {p.email && <div className="pl-sub">{p.email}</div>}
                        </>
                      )
                    ) : (
                      <>
                        {p.telefono || "-"}
                        {p.email && <div className="pl-sub">{p.email}</div>}
                      </>
                    )}
                  </td>
                )}

                <td className="pl-actions">
                  <button
                    className="btn-view"
                    title="Ver"
                    onClick={() => goView(p.id)}
                  >
                    <i className="fa fa-eye" />
                  </button>

                  {isAdmin && (
                    <>
                      <button
                        className="btn-edit"
                        title="Editar"
                        onClick={() => openEdit(p.id)}
                      >
                        <i className="fa fa-pen" />
                      </button>
                      <button
                        className="btn-del"
                        title="Eliminar"
                        onClick={() => handleDelete(p.id)}
                      >
                        <i className="fa fa-trash" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mobile-only mobile-carrusel">
        {loading ? (
          <div className="pl-empty">Cargando pacientes...</div>
        ) : items.length === 0 ? (
          <div className="pl-empty">No hay pacientes disponibles.</div>
        ) : (
          <>
            <button
              className="arrow left"
              onClick={() =>
                setCurrent((prev) => (prev === 0 ? items.length - 1 : prev - 1))
              }
              disabled={items.length <= 1}
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>

            <div className="card-cita">
              <h3>{items[current]?.nombre}</h3>

              <p>
                <strong>Nacimiento:</strong>{" "}
                {formatDate(
                  items[current]?.fechaNacimiento || items[current]?.fecha_nacimiento
                )}
              </p>

              <p>
                <strong>Sexo:</strong> {items[current]?.sexo || "-"}
              </p>

              {isAdmin && (
                <p>
                  <strong>Colegio:</strong> {items[current]?.colegio || "-"}
                </p>
              )}

              <p>
                <strong>Especialidades:</strong>{" "}
                {(() => {
                  const p = items[current];
                  if (!p) return "-";
                  if (
                    Array.isArray(p.servicios) &&
                    p.servicios.length
                  ) {
                    return p.servicios
                      .map(normalizeSpecialtyCode)
                      .join(", ");
                  }

                  if (
                    typeof p.servicios === "string" &&
                    p.servicios.trim()
                  ) {
                    return p.servicios
                      .split(",")
                      .map(normalizeSpecialtyCode)
                      .join(", ");
                  }
                  return "-";
                })()}
              </p>

              <p>
                <strong>Tutor:</strong> {items[current]?.tutor?.nombre || "-"}
              </p>

              {isAdmin && (
                <>
                  <p>
                    <strong>Email:</strong> {items[current]?.tutor?.email || "-"}
                  </p>
                  <p>
                    <strong>Teléfono:</strong> {items[current]?.tutor?.telefono || "-"}
                  </p>
                </>
              )}

              <div className="pl-actions mobile-actions">
                <button
                  className="btn-view"
                  title="Ver"
                  onClick={() => goView(items[current].id)}
                >
                  <i className="fa fa-eye" />
                </button>

                {isAdmin && (
                  <>
                    <button
                      className="btn-edit"
                      title="Editar"
                      onClick={() => openEdit(items[current].id)}
                    >
                      <i className="fa fa-pen" />
                    </button>

                    <button
                      className="btn-del"
                      title="Eliminar"
                      onClick={() => handleDelete(items[current].id)}
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </>
                )}
              </div>
            </div>

            <button
              className="arrow right"
              onClick={() =>
                setCurrent((prev) => (prev === items.length - 1 ? 0 : prev + 1))
              }
              disabled={items.length <= 1}
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </>
        )}
      </div>

      <div className="pl-pager">
        <button disabled={!hasPrev} onClick={() => setPage((p) => p - 1)}>
          ◀ Anterior
        </button>
        <span>
          Página {res.page || page} de {pages}
        </span>
        <button disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Siguiente ▶
        </button>
      </div>

      {editOpen && (
        <div className="modal-backdrop" onClick={closeEdit}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Editar paciente</h3>
            {msg && <div className="notice">{msg}</div>}

            <form onSubmit={saveEdit} className="edit-form">
              <label>Nombre:</label>
              <input
                name="nombre"
                value={form.nombre}
                onChange={onChange}
                required
              />

              <label>Fecha de nacimiento:</label>
              <input
                type="date"
                name="fechaNacimiento"
                value={form.fechaNacimiento}
                onChange={onChange}
                required
              />

              <label>Sexo:</label>
              <select
                name="sexo"
                value={form.sexo}
                onChange={onChange}
                required
              >
                <option value="">Seleccione...</option>
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
              </select>

              <label>Colegio:</label>
              <input name="colegio" value={form.colegio} onChange={onChange} />

              <label>Dirección:</label>
              <input
                name="direccion"
                value={form.direccion}
                onChange={onChange}
                placeholder="Ej: Calle Mayor 10"
              />

              <label>Especialidades:</label>
              <div className="checkbox-group">
                {allSpecs.length === 0 ? (
                  <div style={{ opacity: 0.8 }}>
                    No se pudieron cargar las especialidades
                  </div>
                ) : (
                  allSpecs.map((sp) => (
                    <label key={sp.id}>
                      <input
                        type="checkbox"
                        checked={
                          Array.isArray(form.specialty_ids) &&
                          form.specialty_ids.includes(Number(sp.id))
                        }
                        onChange={(e) =>
                          toggleSpecialtyId(sp.id, e.target.checked)
                        }
                      />
                      {sp.nombre}{" "}
                      <span style={{ opacity: 0.6 }}>({sp.code})</span>
                    </label>
                  ))
                )}
              </div>

              <label>Observaciones:</label>
              <textarea
                name="observaciones"
                value={form.observaciones}
                onChange={onChange}
              />

              <h4>Datos del tutor legal</h4>

              <label>Nombre:</label>
              <input name="tutor" value={form.tutor} onChange={onChange} />

              <label>DNI:</label>
              <input
                name="tutor_dni"
                value={form.tutor_dni}
                onChange={onChange}
              />

              <label>Teléfono del tutor:</label>
              <input
                name="tutor_telefono"
                value={form.tutor_telefono}
                onChange={onChange}
              />

              <label>Email del tutor:</label>
              <input name="email" value={form.email} onChange={onChange} />

              <label>Teléfono de emergencia:</label>
              <input
                name="emergencia"
                value={form.emergencia}
                onChange={onChange}
              />

              <div className="modal-actions">
                <button type="submit" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar cambios"}
                </button>
                <button type="button" className="cancel" onClick={closeEdit}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}